import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { statisticsApi } from '../api/statistics'
import type { BotStatsTimeSeriesResponse, BotDailyMetric, Granularity } from '../types/statistics'

interface UseBotStatisticsOptions {
  enabled?: boolean
  refresh?: boolean // Force backend cache bypass
}

export const useBotStatistics = (
  botId: string | undefined,
  startDate: string,
  endDate: string,
  granularity: Granularity,
  options?: UseBotStatisticsOptions
): UseQueryResult<BotStatsTimeSeriesResponse, Error> => {
  return useQuery({
    queryKey: ['bot-statistics', botId, startDate, endDate, granularity, options?.refresh],
    queryFn: () => {
      if (!botId) throw new Error('Bot ID is required')
      return statisticsApi.getBotTimeSeries(
        botId,
        startDate,
        endDate,
        granularity,
        options?.refresh
      )
    },
    enabled: options?.enabled && !!botId,
    staleTime: Infinity, // Never becomes stale - trust backend cache completely
    gcTime: 60 * 60 * 1000, // 60 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Don't auto-refetch on window focus
    refetchOnMount: false, // Don't auto-refetch on component mount
    refetchOnReconnect: false, // Don't auto-refetch on reconnect
    refetchInterval: false, // Disable interval refetching
    refetchIntervalInBackground: false, // Disable background interval refetching
  })
}

export const useBotLatestMetrics = (
  botId: string | undefined,
  days: number = 7,
  options?: UseBotStatisticsOptions
): UseQueryResult<BotDailyMetric[], Error> => {
  return useQuery({
    queryKey: ['bot-latest-metrics', botId, days],
    queryFn: () => {
      if (!botId) throw new Error('Bot ID is required')
      return statisticsApi.getBotLatestMetrics(botId, days)
    },
    enabled: options?.enabled && !!botId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
}
